import { requestMessengerV2Json } from './messengerV2Api.js';

const CALL_REQUEST_TIMEOUT_MS = 12000;

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const requestJson = (path, options = {}) =>
  requestMessengerV2Json(
    path,
    {
      cache: 'no-store',
      timeoutMs: CALL_REQUEST_TIMEOUT_MS,
      ...options,
    },
    'Unable to complete the call request',
  );

export const loadMessengerV2SfuCallConfig = async ({ conversationId } = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) {
    throw new Error('conversationId is required');
  }

  return requestJson(
    `/api/conversations/${encodeURIComponent(normalizedConversationId)}/calls/config`,
  );
};

export const createMessengerV2SfuCall = async ({
  conversationId,
  mode = 'voice',
  clientId = null,
  idempotencyKey = null,
  connectionId = null,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  const normalizedClientId = toStringOrNull(clientId);
  const normalizedIdempotencyKey = toStringOrNull(idempotencyKey);
  if (!normalizedConversationId) {
    throw new Error('conversationId is required');
  }

  return requestJson(`/api/conversations/${encodeURIComponent(normalizedConversationId)}/calls`, {
    method: 'POST',
    body: JSON.stringify({
      mode: mode === 'video' ? 'video' : 'voice',
      ...(connectionId ? { connection_id: connectionId } : {}),
      ...(normalizedClientId ? { client_id: normalizedClientId } : {}),
    }),
    ...(normalizedClientId || normalizedIdempotencyKey
      ? {
          headers: {
            ...(normalizedClientId ? { 'X-MLS-Client-Id': normalizedClientId } : {}),
            ...(normalizedIdempotencyKey ? { 'Idempotency-Key': normalizedIdempotencyKey } : {}),
          },
        }
      : {}),
  });
};

export const loadActiveMessengerV2SfuCalls = async () => requestJson('/api/calls/active');

export const loadMessengerV2SfuCallInvite = ({ conversationId, callId }) =>
  requestJson(`/api/conversations/${encodeURIComponent(conversationId)}/calls/${encodeURIComponent(callId)}/invite`);

export const loadMessengerV2SfuCall = async ({ conversationId, callId } = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  const normalizedCallId = toStringOrNull(callId);
  if (!normalizedConversationId || !normalizedCallId) return null;

  return requestJson(
    `/api/conversations/${encodeURIComponent(normalizedConversationId)}/calls/${encodeURIComponent(
      normalizedCallId,
    )}`,
  );
};

export const joinMessengerV2SfuCall = async ({ conversationId, callId, clientId, connectionId = null } = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  const normalizedCallId = toStringOrNull(callId);
  const normalizedClientId = toStringOrNull(clientId);
  if (!normalizedConversationId) {
    throw new Error('conversationId is required');
  }
  if (!normalizedCallId) {
    throw new Error('callId is required');
  }
  if (!normalizedClientId) {
    throw new Error('clientId is required');
  }

  return requestJson(
    `/api/conversations/${encodeURIComponent(normalizedConversationId)}/calls/${encodeURIComponent(
      normalizedCallId,
    )}/join`,
    {
      method: 'POST',
      headers: {
        'X-MLS-Client-Id': normalizedClientId,
      },
      body: JSON.stringify({ client_id: normalizedClientId, ...(connectionId ? { connection_id: connectionId } : {}) }),
    },
  );
};

const CALL_TRANSITIONS = new Set([
  'ring',
  'acknowledge',
  'decline',
  'cancel',
  'heartbeat',
  'leave',
  'end',
]);

export const transitionMessengerV2SfuCall = async ({
  conversationId,
  callId,
  action,
  clientId = null,
  payload = {},
  keepalive = false,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  const normalizedCallId = toStringOrNull(callId);
  const normalizedClientId = toStringOrNull(clientId);
  const normalizedAction = toStringOrNull(action)?.toLowerCase();
  if (!normalizedConversationId || !normalizedCallId) return null;
  if (!CALL_TRANSITIONS.has(normalizedAction)) {
    throw new Error('Unsupported call transition');
  }

  return requestJson(
    `/api/conversations/${encodeURIComponent(normalizedConversationId)}/calls/${encodeURIComponent(
      normalizedCallId,
    )}/${normalizedAction}`,
    {
      method: 'POST',
      keepalive: keepalive === true,
      ...(normalizedClientId ? { headers: { 'X-MLS-Client-Id': normalizedClientId } } : {}),
      body: JSON.stringify({
        ...payload,
        ...(normalizedClientId ? { client_id: normalizedClientId } : {}),
      }),
    },
  );
};

export const leaveMessengerV2SfuCall = async ({ conversationId, callId } = {}) => {
  return transitionMessengerV2SfuCall({ conversationId, callId, action: 'leave' });
};

export const endMessengerV2SfuCall = async ({ conversationId, callId } = {}) => {
  return transitionMessengerV2SfuCall({ conversationId, callId, action: 'end' });
};

const sfuCallService = {
  loadMessengerV2SfuCallConfig,
  loadActiveMessengerV2SfuCalls,
  loadMessengerV2SfuCall,
  createMessengerV2SfuCall,
  joinMessengerV2SfuCall,
  transitionMessengerV2SfuCall,
  leaveMessengerV2SfuCall,
  endMessengerV2SfuCall,
};

export default sfuCallService;
